import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Row from "antd/es/row";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Button, Popconfirm } from "@mapmycustomers/ui";

import NylasType from "@app/enum/NylasType";
import { isNylasCalendarSetupFinished, isNylasConnectionFailed } from "@app/store/nylas";
import { deleteNylas } from "@app/store/nylas/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import NylasInfo from "@app/types/nylas/NylasInfo";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import InfoIcon from "./component/InfoIcon";
import styles from "./InfoField.module.scss";
import getInfoText from "./util/getInfoText";

interface Props {
  analyticIssuer?: AnalyticsService;
  deleteNylas: typeof deleteNylas.request;
  hideIfFinished?: boolean;
  isSetupFinished: boolean;
  isSyncFailed: boolean;
  nylasInfo: NylasInfo;
  onDelete?: () => void;
  onShowPane?: () => void;
  onSync?: () => void;
}

const InfoField: React.FC<Props> = ({
  analyticIssuer,
  deleteNylas,
  hideIfFinished,
  isSetupFinished,
  isSyncFailed,
  nylasInfo,
  onDelete,
  onShowPane,
  onSync,
}) => {
  const intl = useIntl();

  const handleDisconnect = useCallback(() => {
    analyticIssuer?.clicked(["Disconnect"]);
    onDelete?.();
    deleteNylas({ nylasId: nylasInfo.id, type: NylasType.CALENDAR });
  }, [analyticIssuer, deleteNylas, nylasInfo.id, onDelete]);

  const handleEditClick = useCallback(() => {
    if (isSetupFinished) {
      analyticIssuer?.clicked(["Edit"]);
    } else {
      analyticIssuer?.clicked(["Finish Setup"]);
    }
    onShowPane?.();
  }, [analyticIssuer, isSetupFinished, onShowPane]);

  if (hideIfFinished && isSetupFinished) {
    return null;
  }

  return (
    <Row
      align="middle"
      className={cn(styles.container, {
        [styles.error]: isSyncFailed,
        [styles.warning]: !isSetupFinished,
      })}
      justify="space-between"
    >
      <Col span={14}>
        <Row align="middle" gutter={layout.spacerM}>
          <Col span={2}>
            <InfoIcon isSyncFailed={isSyncFailed} />
          </Col>
          <Col span={22}>{getInfoText(intl, isSetupFinished, isSyncFailed)}</Col>
        </Row>
      </Col>
      <Col span={10}>
        <Row align="middle" gutter={layout.spacerM} justify="end">
          <Col>
            <Popconfirm
              cancelText={intl.formatMessage({
                id: "settings.calendar.infoField.disconnect.confirm.no",
                defaultMessage: "No",
                description: "Title of settings calendar info field - disconnect cancel",
              })}
              description={intl.formatMessage({
                id: "settings.calendar.infoField.disconnect.confirm.title",
                defaultMessage: "Are you sure you want to disconnect this sync?",
                description:
                  "Title of settings calendar info field - disconnect confirmation title",
              })}
              okText={intl.formatMessage({
                id: "settings.calendar.infoField.disconnect.confirm.yes",
                defaultMessage: "Yes",
                description: "Title of settings calendar info field - disconnect cancel",
              })}
              onConfirm={handleDisconnect}
              placement="left"
            >
              <Button size="small">
                {intl.formatMessage({
                  id: "settings.calendar.infoField.disconnect",
                  defaultMessage: "Disconnect",
                  description: "Settings calendar page info field - disconnect",
                })}
              </Button>
            </Popconfirm>
          </Col>
          <Col>
            {isSyncFailed && (
              <Button onClick={onSync} size="small" type="primary">
                {intl.formatMessage({
                  id: "settings.calendar.infoField.resync",
                  defaultMessage: "Re-authorize",
                  description: "Settings calendar page info field - resync",
                })}
              </Button>
            )}
            {onShowPane && !isSyncFailed && (
              <Button onClick={handleEditClick} size="small" type="primary">
                {isSetupFinished
                  ? intl.formatMessage({
                      id: "settings.calendar.infoField.edit",
                      defaultMessage: "Edit",
                      description: "Settings calendar page info field - edit",
                    })
                  : intl.formatMessage({
                      id: "settings.calendar.infoField.finishSetup",
                      defaultMessage: "Finish Setup",
                      description: "Settings calendar page info field - Finish Setup",
                    })}
              </Button>
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

const mapStateToProps = (state: RootState) => ({
  isSetupFinished: isNylasCalendarSetupFinished(state),
  isSyncFailed: isNylasConnectionFailed(state),
});

const mapDispatchToProps = {
  deleteNylas: deleteNylas.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(InfoField);
